import api from '@/apis'
import { request } from '@/utils/storeRequest'
import { createLatestOnly } from '@/utils/latestOnly'
import { createDataVersion } from '@/utils/dataVersion'

// REQUEST OWNERSHIP — re-dispatched after every write on this screen, so two can be in flight with
// the SAME parameters and DIFFERENT data, which is why 'takes no parameters' is not an exemption.
const service_types_request = createLatestOnly()

// DATA CURRENCY — a field write the server has ACCEPTED, recorded so a snapshot taken before it
// cannot put the old value back. Ownership cannot answer this: the stale read is not superseded by
// anything (a toggle dispatches no new read), it is simply older than a write it does not contain.
// Without this, flipping Booking on and having an in-flight refresh land afterwards restored
// `booking_enabled: 0` permanently, in BOTH `queueSetting.items` and `general.queues`.
const service_type_data = createDataVersion()

// ONE place that records an accepted service-type write and reconciles what is on screen. Two
// actions hit `api.updateServiceType` with the same shape and write the same rows; the second one
// recording nothing is exactly how round 10's finding #3 was left with a live sibling.
//
// The write is ANNOUNCED before the request and only records the fields it still owns: ledger order
// is response-arrival order, and replaying a superseded value over fresher server truth made it
// stick, which is worse than not recording at all.
function beginWrite(params) {
  const { service_type_id, ...patch } = params || {}
  return { service_type_id, write: service_type_data.beginWrite(service_type_id, patch) }
}

// MUTATION, not a read: the server has already done this, so a SUCCESS always reconciles. Recorded
// BEFORE any commit — `commit` runs Vuex subscribers and a throwing one propagates, which would
// leave the write unrecorded and a stale snapshot free to undo it.
function recordAccepted(commit, rootState, { service_type_id, write }) {
  const patch = write.accepted()
  if (!Object.keys(patch).length) return
  for (const [type, value] of Object.entries(patch)) {
    commit('UPDATE_QUEUE_ITEM', { service_type_id, type, value })
  }
  commit(
    'SET_GENERAL_QUEUES',
    ((rootState && rootState.general && rootState.general.queues) || []).map(queue => (queue.service_type_id === service_type_id ? { ...queue, ...patch } : queue))
  )
}

export default {
  state: {
    loading: true,
    // Independent snapshot of prefixes keyed by service_type_id, used to revert
    // an edited prefix when validation or the backend rejects the change.
    prefix_baseline: {},
    headers: [
      { text: 'Av waiting (Min)', value: 'average_waiting_time', width: '100', sortable: false },
      { text: 'Queue', value: 'prefix', width: '120', sortable: false },
      { text: 'Service Type', value: 'service_type', width: '160', sortable: false },
      { text: 'Booking', value: 'booking_enabled', width: '90', sortable: false },
      { text: 'Number Start', value: 'number_start', width: '100', sortable: false },
      { text: 'Number Padding', value: 'number_padding', width: '100', sortable: false },
      { text: 'No. of ticket before Keypad trigger', value: 'numpad_threshold', width: '120', sortable: false },
      { text: 'No. of ticket before SMS trigger', value: 'sms_threshold', width: '115', sortable: false },
      { text: 'Threshold(Mins)', value: 'threshold', sortable: false },
      { text: 'Action', value: 'action', width: '70', sortable: false }
    ],
    items: []
  },
  mutations: {
    RESET_QUEUE_SETTING_VIEW(state) {
      state.loading = true
      state.items = []
    },
    SET_QUEUE_SETTING_LOADING(state, payload) {
      state.loading = payload
    },
    SET_QUEUE_SETTING_ITEMS(state, payload) {
      state.items = payload
    },
    SET_QUEUE_PREFIX_BASELINE(state, payload) {
      const baseline = {}
      payload.forEach(item => {
        baseline[item.service_type_id] = item.prefix
      })
      state.prefix_baseline = baseline
    },
    UPDATE_QUEUE_ITEM(state, { service_type_id, type, value }) {
      const index = state.items.findIndex(item => item.service_type_id === service_type_id)
      // A concurrent reset (RESET_QUEUE_SETTING_VIEW) or a refresh that no longer lists this queue
      // leaves nothing to edit. `state.items[-1][type] = value` threw; there is simply no row.
      if (index === -1) return
      state.items[index][type] = value
    }
  },
  actions: {
    async resetQueue({ commit }) {
      try {
        commit('SET_SNACKBAR', { status: true, color: 'loading', text: 'Resetting...', loading: true })
        const response = await api.resetQueue()
        if (response.data.success) {
          commit('SET_SNACKBAR', { status: true, color: 'success', text: 'Success', icon: 'check', timeout: '2000' })
        } else {
          commit('SET_SNACKBAR', { status: true, color: 'error', text: response.data.message, icon: 'close', timeout: '2000' })
        }
      } catch (error) {
        commit('SET_SNACKBAR', { status: true, color: 'error', text: 'Server Error', icon: 'close', timeout: '2000' })
      }
    },
    // Returns whether the list was actually reloaded: removeQueue() speaks after this and must
    // not let its generic failure message be mistaken for "the delete failed".
    async fetchServiceTypeList({ commit }) {
      const isLatest = service_types_request()
      // Taken BEFORE the request goes out — that is the moment the server's view is fixed for it.
      const read = service_type_data.beginRead()
      try {
        commit('RESET_QUEUE_SETTING_VIEW')
        const response = await api.getServiceTypes()
        // A newer request of this kind owns the view now — see utils/latestOnly.js.
        //
        // Returns NULL — neither true nor false. This action's answer is read by removeQueue to
        // decide whether to warn that the list is stale, and a superseded reload cannot answer
        // that from its own response: measured both ways, `true` claims the list is current while
        // the winner may still fail, and `false`/`undefined` makes a SUCCESSFUL delete report a
        // reload failure. A third state lets the caller say nothing rather than guess.
        if (!isLatest()) return null
        if (response.data.success) {
          // RECONCILE FORWARD: replay every write the server accepted AFTER this request went out.
          // Computed once and shared by all four writers, so the two stores cannot disagree — the
          // failure being fixed was `queueSetting.items` and `general.queues` holding different
          // values for the same queue.
          const rows = read.reconcile(response.data.data, item => item.service_type_id)
          commit('SET_GENERAL_QUEUES', rows)
          commit('SET_QUEUE_SETTING_ITEMS', rows)
          commit('SET_QUEUE_PREFIX_BASELINE', rows)
          commit('SET_MENU_SETTING_SERVICE_TYPE_LIST', rows)
          commit('SET_QUEUE_SETTING_LOADING', false)
          return true
        }
        // RESET_QUEUE_SETTING_VIEW put the table back into its loading state, so every exit has
        // to clear it — otherwise a refused refresh leaves the screen spinning for good.
        commit('SET_QUEUE_SETTING_LOADING', false)
        commit('SET_SNACKBAR', { status: true, color: 'error', text: response.data.message, icon: 'close', timeout: '3000' })
        return false
      } catch (error) {
        if (!isLatest()) return
        commit('SET_QUEUE_SETTING_LOADING', false)
        commit('SET_SNACKBAR', { status: true, color: 'error', text: 'Server Error', icon: 'close', timeout: '2000' })
        return false
      } finally {
        // EVERY exit — success, failure, superseded, throw.
        read.settle()
      }
    },
    // Serves the Threshold / Numpad / SMS columns, all bound with `v-model` so the edit is
    // optimistic. Same endpoint and same rows as updateServiceTypeField — a sibling call site of
    // round 10's finding #3, in the same file, which recorded nothing at all.
    async updateServiceTypeThreshold({ commit, rootState }, payload) {
      const write = beginWrite(payload && payload.params)
      let answered = false
      try {
        const response = await api.updateServiceType(payload)
        answered = true
        commit('SET_SNACKBAR', { status: true, color: 'loading', text: 'Updating...', loading: true })
        if (response.data.success) {
          recordAccepted(commit, rootState, write)
          commit('SET_SNACKBAR', { status: true, color: 'success', text: 'Success', icon: 'check', timeout: '2000' })
        } else {
          commit('SET_SNACKBAR', { status: true, color: 'error', text: response.data.message, icon: 'close', timeout: '2000' })
        }
      } catch (error) {
        if (!answered) commit('SET_SNACKBAR', { status: true, color: 'error', text: 'Server Error', icon: 'close', timeout: '2000' })
      }
    },
    // Create / delete return a boolean so the caller only closes the dialog or refreshes
    // the list once the write is confirmed.
    createServiceType({ commit }, payload) {
      return request(commit, { call: () => api.createServiceType(payload), loading: 'Creating queue...', success: 'Queue created.' })
    },
    // Not routed through request(): a delete that goes through still has side effects the
    // operator has to know about (operator skillsets cascade-deleted, past SMS logs unlinked),
    // and the backend already words them — repeating them here would let the two drift apart.
    // So the response message is shown verbatim, and the structured `cascade_deleted` counts
    // only decide how loudly.
    //
    // Returns the OUTCOME TEXT on success (a truthy string) and `false` on failure, so the view
    // can append to it rather than replace it if the reload that follows goes wrong.
    async deleteServiceType({ commit }, payload) {
      let answered = false
      try {
        commit('SET_SNACKBAR', { status: true, color: 'loading', text: 'Deleting queue...', loading: true })
        const response = await api.deleteServiceType(payload)
        answered = true
        // The backend refuses to delete a queue that is still referenced (waiting tickets, menu
        // routes, import profiles, bookings, past tickets) and says which — give it room to be read.
        if (!response.data.success) {
          commit('SET_SNACKBAR', { status: true, color: 'error', text: response.data.message, icon: 'close', timeout: '6000' })
          return false
        }
        // The queue is gone on the server; record it before any commit so a refresh already in
        // flight cannot list it again.
        service_type_data.removed(payload && payload.params && payload.params.service_type_id)
        const cascade = (response.data.data && response.data.data.cascade_deleted) || {}
        const skillsets = Number(cascade.user_skillsets || 0)
        const sms_logs = Number(cascade.sms_logs_unlinked || 0)
        const cascaded = skillsets > 0 || sms_logs > 0
        // The backend words the side effects itself, so its message wins. But if a response ever
        // arrives without one, the counts must still be reported rather than replaced by a fixed
        // "Queue deleted." — losing them silently is the failure this whole change is about.
        const side_effects = []
        if (skillsets) side_effects.push(`${skillsets} operator skillset assignment(s) were removed with it`)
        if (sms_logs) side_effects.push(`${sms_logs} SMS log row(s) kept their content but lost their queue association`)
        const fallback = side_effects.length ? `Queue deleted. ${side_effects.join('; ')}.` : 'Queue deleted.'
        const outcome = response.data.message || fallback
        commit('SET_SNACKBAR', {
          status: true,
          color: cascaded ? 'warning' : 'success',
          text: outcome,
          icon: cascaded ? 'alert' : 'check',
          timeout: cascaded ? '8000' : '3000'
        })
        return outcome
      } catch (error) {
        if (!answered) commit('SET_SNACKBAR', { status: true, color: 'error', text: 'Server Error', icon: 'close', timeout: '3000' })
        return false
      }
    },
    // Update a service type field and return whether it succeeded so the view can react
    // (e.g. re-seed queues after a successful prefix change, or revert the row on failure).
    async updateServiceTypeField({ commit, rootState }, payload) {
      // Announced BEFORE the request: two `@change` writes to one field can be in flight at once.
      const write = beginWrite(payload && payload.params)
      // Once the server has ANSWERED, a local failure is not a server failure — this action now
      // commits after the response, and `commit` runs Vuex subscribers that can throw. Reporting
      // "Server Error" there tells the operator their change failed when it was accepted, and the
      // view then dispatches a refresh to "revert" it.
      let answered = false
      try {
        commit('SET_SNACKBAR', { status: true, color: 'loading', text: 'Updating...', loading: true })
        const response = await api.updateServiceType(payload)
        answered = true
        if (response.data.success) {
          recordAccepted(commit, rootState, write)
          commit('SET_SNACKBAR', { status: true, color: 'success', text: 'Success', icon: 'check', timeout: '2000' })
          return true
        } else {
          commit('SET_SNACKBAR', { status: true, color: 'error', text: response.data.message, icon: 'close', timeout: '2000' })
          return false
        }
      } catch (error) {
        if (!answered) commit('SET_SNACKBAR', { status: true, color: 'error', text: 'Server Error', icon: 'close', timeout: '2000' })
        return false
      }
    }
  }
}
